import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SSOProviderEnum } from '../../constants/auth.constant';
import { ISingleResponseApiModel } from '../../models/base-api-general.model';
import {
    ILoginApiModel,
    ILoginForm,
    IRegisterCompanyForm,
    IRegisterProfileForm,
    IRegisterProfileOAuthForm,
    IResetPasswordForm,
} from '../../models/login.model';
import { LoginService } from '../storage/login.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private fullPathEndPoint = `${environment.apiServer}/api/v1/Users`;

    constructor(
        private http: HttpClient,
        private loginStorage: LoginService,
    ) {}

    logout(): Observable<void> {
        return this.http.post<void>(`${this.fullPathEndPoint}/logout`, {});
    }

    loginOauth(oAuthToken: string, provider: SSOProviderEnum): Observable<ILoginApiModel> {
        const body = {
            device: this.loginStorage.getDevice(),
            provider: provider,
            oAuthToken,
        };

        return this.http
            .post<ISingleResponseApiModel<ILoginApiModel>>(`${this.fullPathEndPoint}/loginOauth`, body, {})
            .pipe(
                map((data) => {
                    this.loginStorage.setFromLogin({ ...data.result, uiProviderType: provider });
                    return data.result;
                }),
            );
    }

    login(body: ILoginForm): Observable<ILoginApiModel> {
        return this.http.post<ISingleResponseApiModel<ILoginApiModel>>(`${this.fullPathEndPoint}/login`, body, {}).pipe(
            map((data) => {
                this.loginStorage.setFromLogin(data.result);
                return data.result;
            }),
        );
    }

    refreshToken(): Observable<ILoginApiModel> {
        let headers = new HttpHeaders();
        headers = headers.set('Content-Type', 'application/json');
        headers = headers.set('Authorization', `Bearer ${this.loginStorage.getRefreshToken()}`);

        return this.http
            .post<
                ISingleResponseApiModel<ILoginApiModel>
            >(`${this.fullPathEndPoint}/refreshToken`, null, { params: { noToken: true }, headers: headers })
            .pipe(
                map((res) => res.result),
                tap((res) => {
                    this.loginStorage.setFromLogin(res);
                }),
            );
    }

    registerEmail(body: { email: string }): Observable<{ emailToken: string }> {
        return this.http
            .post<
                ISingleResponseApiModel<{ emailToken: string }>
            >(`${this.fullPathEndPoint}/requestRegistrationEmailOtp`, body)
            .pipe(map((res) => res.result));
    }

    registerProfile(body: IRegisterProfileForm, emailToken: string): Observable<ILoginApiModel> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${emailToken}`,
        });

        return this.http
            .post<ISingleResponseApiModel<ILoginApiModel>>(`${this.fullPathEndPoint}/onboarding/profile`, body, {
                headers,
                params: { noToken: true },
            })
            .pipe(
                map((data) => {
                    this.loginStorage.setFromLogin(data.result);
                    return data.result;
                }),
            );
    }

    registerProfileOAuth(body: IRegisterProfileOAuthForm, emailToken: string): Observable<ILoginApiModel> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${emailToken}`,
        });

        return this.http
            .post<ISingleResponseApiModel<ILoginApiModel>>(`${this.fullPathEndPoint}/onboarding/profile/Oauth`, body, {
                headers,
                params: { noToken: true },
            })
            .pipe(
                map((data) => {
                    this.loginStorage.setFromLogin(data.result);
                    return data.result;
                }),
            );
    }

    registerCompany(body: IRegisterCompanyForm): Observable<ILoginApiModel> {
        return this.http
            .post<ISingleResponseApiModel<ILoginApiModel>>(`${this.fullPathEndPoint}/onboarding/companyInfo`, body)
            .pipe(
                map((data) => {
                    this.loginStorage.setFromLogin(data.result);
                    return data.result;
                }),
            );
    }

    verifyRegisterEmail(body: { otpCode: string }, emailToken: string): Observable<boolean> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${emailToken}`,
        });

        return this.http
            .post<
                ISingleResponseApiModel<boolean>
            >(`${this.fullPathEndPoint}/verifyRegistrationEmail`, body, { headers, params: { noToken: true } })
            .pipe(map((res) => res.result));
    }

    resendRegisterOtp(body: { email: string }): Observable<boolean> {
        return this.http
            .post<ISingleResponseApiModel<boolean>>(`${this.fullPathEndPoint}/resendRegistrationEmailOtp`, body)
            .pipe(map((res) => res.result));
    }

    forgotPasswordEmail(body: { email: string; reCaptchaToken: string }): Observable<{ accessToken: string }> {
        return this.http
            .post<ISingleResponseApiModel<{ accessToken: string }>>(`${this.fullPathEndPoint}/forgetPassword`, body, {
                params: { noToken: true },
            })
            .pipe(map((res) => res.result));
    }

    verifyForgotPassword(body: { otpCode: string }, accessToken: string): Observable<boolean> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
        });

        return this.http
            .post<ISingleResponseApiModel<boolean>>(`${this.fullPathEndPoint}/verifyOtp`, body, {
                headers,
                params: { noToken: true },
            })
            .pipe(map((res) => res.result));
    }

    resendForgotPasswordOtp(body: { email: string }): Observable<{ accessToken: string }> {
        return this.http
            .post<ISingleResponseApiModel<{ accessToken: string }>>(`${this.fullPathEndPoint}/resendOtp`, body, {
                params: { noToken: true },
            })
            .pipe(map((res) => res.result));
    }

    resetPassword(body: IResetPasswordForm, accessToken: string): Observable<boolean> {
        const headers = new HttpHeaders({
            Authorization: `Bearer ${accessToken}`,
        });

        return this.http
            .post<ISingleResponseApiModel<boolean>>(`${this.fullPathEndPoint}/resetPassword`, body, {
                headers,
                params: { noToken: true },
            })
            .pipe(map((res) => res.result));
    }
}
